import {
    adoptSession,
    cancelSession,
    closeSession,
    consumeSuppressedOpen,
    editorCellOf,
    editorCellsOf,
    focusEditorCell,
    getSession,
    isEditorElement,
    nextEditableInRow,
    openSession,
} from './resource-entry-editing';

/* ---------------------------------------------------------------------------
   Kendo grid event handlers.

   These translate grid events into requests against the single editing session in
   resource-entry-editing.js and hold no editing state of their own. That is deliberate:
   they previously carried skipEdit, selectNextRow, nextRowIndex, selectColumnIndex,
   lastSelectionRow and lastSelectionRowIndex on the grid object, set them from four
   different handlers across keydown, keyup, click and dataBound, and cleared them on some
   paths but not others. Editing the page was jittery because those handlers disagreed
   about which row should be open and who was allowed to save it.
   --------------------------------------------------------------------------- */

const updateSelect = function(e) {
    const grid = this;
    const cell = $(e.target).closest('td');
    if (!cell.length) return;

    // Compares elements. The old version compared two freshly built jQuery objects with
    // ===, which is never equal, so every focus event rebuilt the selection.
    const current = grid.current();
    if (current && current.length && current[0] === cell[0]) return;

    grid.clearSelection();
    cell.addClass('k-state-selected');
};

export const updateFooter = function(e) { // update column total footers as changes are made, does not support groups currently
    if (!e.field && e.action !== 'itemchange') return;

    const me = this;
    const dataSource = me.dataSource;
    const grid = me.grid;

    calculateAggregates(grid, dataSource);
};

const calculateAggregates = (grid, dataSource) => {
    let aggregates = dataSource.aggregates();

    $.each(aggregates, (field) => {
        let total = 0;
        $.each(dataSource.data(), (index, object) => {
            let value = object[field];
            if (value === null) {
                value = grid.dataSource._pristineData[index][field];
            }
            total += value;
        });
        aggregates[field].sum = total;
    });

    const newFooterValues = $(grid.footerTemplate(aggregates)).find('td');
    const lockedValues = grid.footer.find('.k-grid-footer-locked').find('td');
    const footerValues = grid.footer.find('.k-grid-footer-wrap').find('.k-footer-template').find('td');
    $.each(lockedValues, (i, value) => {
        value.replaceWith(newFooterValues[i]);
    });
    $.each(footerValues, (i, value) => {
        value.replaceWith(newFooterValues[i + grid.staticColumnCount]);
    });
};

/**
 * Tab, Enter and Escape for the open row. One handler on keydown, where previously Tab was
 * split across a keydown handler that jumped to the next row and a keyup handler that
 * rewrote the row, both reading the selection at different points in the same keystroke.
 */
export const editingKeydown = function(e) {
    const { key, shiftKey, data: me } = e;
    if (me.readonly) return;

    if (key === 'Escape') {
        cancelSession();
        calculateAggregates(me.grid, me.dataSource);
        return;
    }

    if (key === 'Enter') {
        // Only a keystroke from inside an editor closes a row. Kendo's navigatable handler is
        // bound on grid.table, inside grid.element where this one is, so it runs first: on a
        // closed row it opens the row itself and the synchronous edit event adopts a session
        // for it. Closing on any Enter would shut that row again in the same keystroke and
        // remove keyboard entry into edit mode, so Enter on a closed cell is left to Kendo.
        if (!isEditorElement(e.target)) return;
        closeSession();
        return;
    }

    if (key !== 'Tab') return;
    if (!getSession()) return;

    // Resolved by cell, because e.target is whichever of the cell's two inputs Kendo left
    // focus on rather than the one focus was aimed at.
    const next = nextEditableInRow(editorCellsOf(me.grid), editorCellOf(e.target), shiftKey);

    // Focus movement inside the row is ours, so the browser's Tab is cancelled either way;
    // otherwise it would carry focus out of the row and into the next one.
    e.preventDefault();

    if (!next) {
        // Closing blurs the editor and the row re-renders without it, so with the default
        // cancelled focus would be left on nothing and the next Tab would restart from the top
        // of the document. Park it on the grid instead, so the next Tab carries on past it.
        closeSession();
        me.grid.table.focus();
        return;
    }

    focusEditorCell(next);
};

/**
 * Presentation only. This used to reopen an editor and move focus on every rebind, decided
 * from flags left on the grid object, so a save on an unrelated grid could pull focus into
 * this one while it was being typed into.
 */
export const dataBound = function(_) {
    const me = this;
    me.options.dataBound(me);
};

export const change = function(_) { // grid selection changed, which is how a press reaches us
    const me = this;
    if (me.readonly) return;

    // The press that closed a row does not open whatever it landed on, even if that was an
    // editable cell on another row.
    if (consumeSuppressedOpen()) return;

    // A press inside the row already open focuses its editor natively; reopening the row
    // here would fight that.
    if (getSession()) return;

    const selection = me.grid.select();
    if (!selection.length || selection.length > 1) return;

    openSession(me, selection);
};

/**
 * Keep the selected cell in step with the focused editor. There is nothing here about
 * saving: closing a row is the session's job, and it used to be triggered from a
 * click.rowSave handler bound to every other row, which saved the open row and then opened
 * the pressed one, so the editor hopped between rows instead of closing.
 */
export const edit = function(_) {
    const me = this;
    if (me.readonly) return;

    // Fires for every row the grid opens, whoever opened it, which makes this the one place
    // that can guarantee an open row has a session and can therefore be closed again.
    adoptSession(me);

    me.grid.element.find('tr.k-grid-edit-row').find('input.k-formatted-value')
        .off('focusin.updateSelect')
        .on('focusin.updateSelect', updateSelect.bind(me.grid));
};
